import React, { useEffect, useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Error } from "../../../../../../../models/error";
import { Filters, Searches, Sort, Sorts, Row } from "../../../../../../../models/shipeezi.table";
import { SpecialInstruction } from "../../../../../../../models/special.instruction";
import SpecialInstructionsService from "../../../../../../../services/special.instruction.service";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import SpecialInstructionsModal from "./SpecialInstructionsModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const SpecialInstructions = ({ permission }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [specialInstructions, setSpecialInstructions] = useState<SpecialInstruction[]>([]);
  const [selectedSpecialInstruction, setSelectedSpecialInstructions] =
    useState<SpecialInstruction>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status?.toLowerCase() === Status.ACTIVE.toLowerCase() ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["special.instructions.list", page, pageSize, filters, searches, sorting],
    () =>
      SpecialInstructionsService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["id", "desc"]])
          : JSON.stringify([["id", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setSpecialInstructions(response.data);
      },
    }
  );

  const { refetch: getSpecialInstructionsTotal } = useQuery(
    ["special.instructions.count", page, pageSize, filters, searches, sorting],
    () =>
      SpecialInstructionsService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: createSpecialInstruction, isLoading: isCreating } = useMutation(
    ["special.instruction.create"],
    (specialInstruction: SpecialInstruction) =>
      SpecialInstructionsService.create(specialInstruction),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasErrors(false);
        refetch();
        getSpecialInstructionsTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateSpecialInstruction, isLoading: isUpdating } = useMutation(
    ["special.instruction.update"],
    ({ id, specialInstruction }: { id: number; specialInstruction: SpecialInstruction }) =>
      SpecialInstructionsService.update(id, specialInstruction),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.success.message.special.instruction",
        });
        refetch();
        getSpecialInstructionsTotal();
        setHasErrors(false);
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const editSpecialInstruction = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const findSpecialInstruction = specialInstructions.find(
      (specialInstruction) => Number(specialInstruction.id) === Number(index)
    );

    if (findSpecialInstruction) {
      setSelectedSpecialInstructions(findSpecialInstruction);
      setOpenModal(true);
    }
  };

  const changeSpecialInstruction = (index: number) => {
    const findSpecialInstruction = specialInstructions.find(
      (specialInstruction) => Number(specialInstruction.id) === Number(index)
    );

    if (findSpecialInstruction) {
      updateSpecialInstruction({
        id: Number(index),
        specialInstruction: {
          ...findSpecialInstruction,
          status: findSpecialInstruction.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => {
            setOpenConfirmation(true);
          }}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveSpecialInstructionSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, specialInstructions);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="specialInstructionsDataList">
      <ShipeeziNewGrid
        data={specialInstructions}
        columns={[
          {
            name: "code",
            title: translate("code"),
          },
          {
            name: "description",
            title: translate("name"),
            formatComponent: (value?: string) => value,
          },
          {
            name: "status",
            hideSearch: true,
            title: translate("status"),
            formatComponent: (status: string) => badgeStatus(status),
          },
        ]}
        totalItems={total}
        loadingData={isFetching || isCreating || isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (specialInstructionId) =>
              editSpecialInstruction(Number(specialInstructionId))
            : undefined,
          handleChange: (specialInstructionId) =>
            changeSpecialInstruction(Number(specialInstructionId)),
        }}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSaveSpecialInstructionSelected(rows)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setSelectedSpecialInstructions(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("code"),
                column: "code",
              },
              {
                name: translate("name"),
                column: "description",
              },
              {
                name: translate("status"),
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <SpecialInstructionsModal
          specialInstruction={selectedSpecialInstruction}
          open={openModal}
          hasError={hasErrors}
          isLoading={isCreating || isUpdating}
          closeModal={() => setOpenModal(false)}
          updateOrCreateSpecialInstruction={(specialInstruction: SpecialInstruction) =>
            selectedSpecialInstruction
              ? selectedSpecialInstruction.id &&
                updateSpecialInstruction({
                  id: selectedSpecialInstruction.id,
                  specialInstruction: {
                    ...selectedSpecialInstruction,
                    ...specialInstruction,
                  },
                })
              : createSpecialInstruction({
                  ...specialInstruction,
                  companyId: currentUser?.companyId,
                  valid: true,
                })
          }
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateSpecialInstruction({
                id: rowId,
                specialInstruction: { status } as SpecialInstruction,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default SpecialInstructions;
